import * as Dialog from '@radix-ui/react-dialog';
import { ErrorBoundary } from '@sentry/react';
import { ReactNode, Suspense, useCallback, useEffect, useState } from 'react';

import { Button, ButtonProps } from '@/components/Button/Button';
import { RegularSpinner } from '@/icons/components';
import { createScopedContext } from '@/utils/createScopedContext/createScopedContext';
import { twJoin } from '@/utils/tw-merge';

import { useManagedModalContext } from '../manager/ModalManager';
import { ModalStateProps } from '../manager/useModalState';

export type ModalBuilderRootProps = {
  children: ReactNode;
  title: string | ReactNode;
  description?: string | ReactNode;
  afterLeave?: () => void;
  forceClose?: boolean;
  isClosingDisabled?: boolean;
  isLoading?: boolean;
  defaultOpen?: boolean;
  beforeClose?: () => void;
  modalStateProps?: ModalStateProps;
};

export type ModalBuilderContentProps = Pick<
  ModalBuilderRootProps,
  'children'
> & {
  customWidth?: `max-w-${string}`;
  size?: 'medium' | 'large' | 'xlarge';
};

const OVERLAY_OPEN_CLASS = 'data-[state=open]:animate-overlayShow';
const OVERLAY_CLOSED_CLASS = 'data-[state=closed]:animate-overlayHide';
const CONTENT_OPEN_CLASS = 'data-[state=open]:animate-contentShow';
const CONTENT_CLOSED_CLASS = 'data-[state=closed]:animate-contentHide';

export const [ModalBuilderProvider, useModalBuilderContext] =
  createScopedContext<
    Pick<ModalBuilderRootProps, 'modalStateProps' | 'title' | 'description'>
  >();

/**
 * Low-level primitives for building custom modal layouts.
 * Handles open/close animation, Suspense, ErrorBoundary, and ModalManager integration.
 *
 * Prefer the pre-built layouts in `layouts/` (e.g. `ModalDefault`) over using
 * these primitives directly. Use `ModalBuilder` only when none of the existing
 * layouts fit and you need full control over the modal's structure.
 *
 * @example
 * function MyModal() {
 *   return (
 *     <ModalBuilder.Root title="Title">
 *       <ModalBuilder.Content size="medium">
 *         <p>Content goes here</p>
 *         <ModalBuilder.Close />
 *         <ModalBuilder.Action variant="primary" onClick={handleSubmit}>
 *           Submit
 *         </ModalBuilder.Action>
 *       </ModalBuilder.Content>
 *     </ModalBuilder.Root>
 *   );
 * }
 */
export const ModalBuilder = {
  Root: ModalBuilderRoot,
  Content: ModalBuilderContent,
  Action: ModalBuilderAction,
  Close: ModalBuilderClose,
};

function ModalBuilderRoot({
  children,
  title,
  description,
  forceClose,
  isClosingDisabled,
  isLoading,
  modalStateProps,
  beforeClose,
  afterLeave,
}: ModalBuilderRootProps) {
  /**
   * When a modal is managed by the ModalManager, it will be wrapped in a ManagedModalContext.Provider
   * The provider contains the modal ID and an afterLeave function to remove the modal from the store
   */
  const managedModalContext = useManagedModalContext();

  const [state, setState] = useState<'open' | 'closing' | 'closed'>('open');

  const open = useCallback(() => setState('open'), []);
  const close = useCallback(() => setState('closing'), []);

  useEffect(() => {
    if (
      forceClose ||
      modalStateProps?.forceClose ||
      managedModalContext?.MANAGED_MODAL_PROP_FORCE_CLOSE
    ) {
      close();
    }
  }, [
    forceClose,
    modalStateProps?.forceClose,
    managedModalContext?.MANAGED_MODAL_PROP_FORCE_CLOSE,
    close,
  ]);

  return (
    <ModalBuilderProvider
      modalStateProps={modalStateProps}
      title={title}
      description={description}
    >
      <Dialog.Root
        open={state === 'open'}
        onOpenChange={(isOpen) => {
          if (isOpen) {
            open();
          } else {
            beforeClose?.();

            if (!isClosingDisabled && !modalStateProps?.isClosingDisabled) {
              close();
            }
          }
        }}
      >
        {['closing', 'open'].includes(state) && (
          <Dialog.Portal forceMount>
            <div
              data-testid="modal"
              onAnimationEnd={(e) => {
                const target = e.target as HTMLElement;

                if (
                  target.classList.contains(OVERLAY_CLOSED_CLASS) ||
                  target.classList.contains(CONTENT_CLOSED_CLASS)
                ) {
                  if (state !== 'open') {
                    setState('closed');

                    afterLeave?.();
                    managedModalContext?.MANAGED_MODAL_PROP_AFTER_LEAVE();
                  }
                }
              }}
            >
              <div className="relative z-modal">
                <Dialog.Overlay
                  className={twJoin(
                    'fixed inset-0 bg-black/20',
                    OVERLAY_OPEN_CLASS,
                    OVERLAY_CLOSED_CLASS
                  )}
                />

                {isLoading ? (
                  <DialogContentLoader />
                ) : (
                  <ErrorBoundary>
                    <Suspense fallback={<DialogContentLoader />}>
                      {children}
                    </Suspense>
                  </ErrorBoundary>
                )}
              </div>
            </div>
          </Dialog.Portal>
        )}
      </Dialog.Root>
    </ModalBuilderProvider>
  );
}

const DialogContentLoader = () => {
  const { title, description } = useModalBuilderContext();
  const managedModalContext = useManagedModalContext();

  return (
    <Dialog.Content
      id={managedModalContext?.MANAGED_MODAL_PROP_ID}
      className={twJoin(
        'fixed left-1/2 top-1/2 flex size-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-md bg-white shadow-md focus:outline-none',
        CONTENT_OPEN_CLASS,
        CONTENT_CLOSED_CLASS
      )}
    >
      <Dialog.Title className="sr-only">{title}</Dialog.Title>

      {description && (
        <Dialog.Description className="sr-only" asChild>
          {description}
        </Dialog.Description>
      )}

      <RegularSpinner size={16} className="animate-spin" />
    </Dialog.Content>
  );
};

function ModalBuilderContent({
  children,
  customWidth,
  size,
}: ModalBuilderContentProps) {
  const managedModalContext = useManagedModalContext();

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4">
      <Dialog.Content
        id={managedModalContext?.MANAGED_MODAL_PROP_ID}
        className={twJoin(
          'flex max-h-full w-full flex-col',
          'rounded-md bg-white shadow-md focus:outline-none',
          customWidth,
          !customWidth && size === 'medium' && 'max-w-[450px]',
          !customWidth && size === 'large' && 'max-w-[800px]',
          !customWidth && size === 'xlarge' && 'max-w-[1280px]',
          CONTENT_OPEN_CLASS,
          CONTENT_CLOSED_CLASS
        )}
      >
        {children}
      </Dialog.Content>
    </div>
  );
}

function ModalBuilderAction(props: Omit<ButtonProps, 'state'>) {
  const { modalStateProps: { action } = {} } = useModalBuilderContext();

  return (
    <Button
      state={
        action?.type === 'pending'
          ? 'loading'
          : action?.type === 'success'
            ? 'success'
            : undefined
      }
      {...props}
    />
  );
}

function ModalBuilderClose({
  disabled,
  label = 'Annuleren',
}: {
  disabled?: boolean;
  label?: string;
}) {
  const { modalStateProps } = useModalBuilderContext();

  return (
    <Dialog.Close asChild>
      <Button
        variant="secondary"
        disabled={disabled ?? modalStateProps?.action?.type === 'pending'}
      >
        {label}
      </Button>
    </Dialog.Close>
  );
}
